import React, { useCallback, useMemo } from 'react';
import { useMediaQuery } from '@material-ui/core';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { flightIconPath, flightClassIconPath, flightClassName, digits } from '@this/src/util';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import type { Tab } from '@this/domain/select_store';
import type FlightSliceCandidate from '@this/domain/flight/flight_slice_candidate';
import { Left, Segment, Name, Image, BoxFooter } from '../transport_box/transport_box_foreign.template';
import {
  BoxPriceArea,
  BoxPriceWrap,
  BoxPrice,
  BoxSmall,
  BoxButtonArea,
  BoxButton
} from '../transport_box/right/right_in_outline.template';
import { BoxButtonText, Yen } from '../shared/box_parts';
import { ChangeCancelButtonLabel, OtherCandidatesButtonLabel } from '../other_candidates_button_label';

export interface FlightBoxProps {
  // Outline/List 共通
  flight: FlightSliceCandidate | undefined;
  selected: boolean;
  inList: boolean;
  isDomestic: boolean;

  // Outline用
  handleClickTab?: (tab: Tab) => void;
  tab: number;
  flightIndex: number;

  // List用
  handleSelect?: (f: FlightSliceCandidate) => void;

  // ArrangementRequestForm
  isRequestForm?: boolean;
  candidateCount?: number;

  showFareTypeBadges?: boolean;
  isLowest?: boolean;
}

const FlightBox: React.FC<FlightBoxProps> = ({
  flight,
  selected,
  inList,
  isDomestic,
  handleClickTab,
  tab,
  flightIndex,
  handleSelect,
  isRequestForm,
  candidateCount = 0,
  showFareTypeBadges = false,
  isLowest = false
}) => {
  const { theme } = useAITTheme();
  const isMobile = useMediaQuery('(max-width:768px)');
  const isSelectSp = useMediaQuery('(max-width:900px)');
  const styles = useMemo(
    () => getStyles(theme, isMobile, selected, isDomestic, isSelectSp),
    [theme, isMobile, selected, isDomestic, isSelectSp]
  );

  const handleChange = useCallback(() => {
    if (inList) {
      if (handleSelect && flight) handleSelect(flight);
    } else if (handleClickTab && tab !== undefined) {
      handleClickTab(selected ? 'none' : tab);
    }
  }, [inList, handleSelect, flight, handleClickTab, tab, selected]);

  const flightSlice = flight?.cheapestFlight()?.itineraries?.[flightIndex];
  const hasTicketingDeadline = Boolean(flightSlice?.rawTicketingExpiredAt || flightSlice?.timeToLive);
  let ticketingDeadlineInner: React.ReactNode = null;
  if (flightSlice && hasTicketingDeadline) {
    if (inList) {
      ticketingDeadlineInner = flightSlice.rawTicketingExpiredAt
        ? flightSlice.rawTicketingExpiredAt.format('YYYY/MM/DD HH:mm')
        : `ご依頼から${flightSlice.timeToLive}時間`;
    } else {
      ticketingDeadlineInner = flightSlice.rawTicketingExpiredAt ? (
        <span data-wovn-ignore>{flightSlice.rawTicketingExpiredAt.format('YYYY/MM/DD HH:mm')}</span>
      ) : (
        <>
          <span>ご依頼から</span>
          <span data-wovn-ignore>{flightSlice.timeToLive}</span>
          <span>時間</span>
        </>
      );
    }
  }

  const footerContent = flight ? (
    <>
      <div>
        <span>所要時間：</span>
        <span data-wovn-ignore>{flightSlice?.duration}</span>
      </div>
      <span>表記は現地時刻です。</span>
      {flight.cheapestFlightMinirulePerPerson() && (
        <span data-wovn-ignore>{`(${flight.cheapestFlightMinirulePerPerson()})`}</span>
      )}
    </>
  ) : null;

  return (
    <div style={styles.flightBox}>
      {flight ? (
        <>
          <div style={styles.flightMainColumn}>
            {showFareTypeBadges && flight && (
              <div style={styles.badgeRow}>
                <span style={flight.fareType() === 'corporate' ? styles.corporateBadge : styles.publishedBadge}>
                  {flight.fareType() === 'corporate' ? '法人運賃' : '通常運賃'}
                </span>
                {isLowest && <span style={styles.lowestBadge}>候補最安値</span>}
              </div>
            )}
            <Left>
              {flightSlice?.segments?.map((s, i) => (
                <Segment key={`segment-${s.from?.name}-${s.to?.name}-${i}`}>
                  <div className="flex">
                    <Name data-wovn-ignore>
                      {s.carrier_name && `${s.carrier_name} `}
                      {s.name}
                    </Name>
                    <Image src={flightIconPath(s.name)} />
                  </div>
                  <div style={styles.flightRow}>
                    <div style={styles.boxTitle}>出発</div>
                    <div data-wovn-ignore style={styles.flightInfo}>
                      {`${s.from.date} ${s.from.time} ${s.from.name}`}
                    </div>
                  </div>
                  <div style={styles.flightRow}>
                    <div style={styles.boxTitle}>到着</div>
                    <div data-wovn-ignore style={styles.flightInfo}>
                      {`${s.to.date} ${s.to.time} ${s.to.name}`}
                    </div>
                  </div>
                  <div style={styles.cabinRow}>
                    <img
                      src={flightClassIconPath(s.cabin)}
                      title={flightClassName(s.cabin)}
                      width={40}
                      height={24}
                      style={{ width: '40px', height: 'auto' }}
                      alt="flight class icon"
                    />
                    <div data-wovn-ignore style={styles.cabinText}>
                      {flightClassName(s.cabin)}
                    </div>
                  </div>
                </Segment>
              ))}
            </Left>
            {inList ? (
              <>
                {flightSlice?.ticketing_enable ? (
                  <div
                    style={styles.rightButton(selected || flight.hovered, null, isDomestic)}
                    className={`flight-box__right-button ${
                      selected || flight.hovered ? 'flight-box__right-button--selected' : ''
                    }`}
                    onClick={handleChange}
                    onTouchStart={flight.handleTouched}
                    onMouseOver={() => flight.setHovered(true)}
                    onMouseLeave={() => flight.setHovered(false)}
                  >
                    {flight.hovered && !selected ? (
                      <div className="select-transport-box__right__body__select">選択する</div>
                    ) : (
                      <div style={styles.rightBody}>
                        {tab === 0 ? (
                          <div>
                            <span style={styles.rightPrice}>
                              <span data-wovn-ignore>{digits(flight.pricePerPerson())}</span>
                              <Yen>円</Yen>
                            </span>
                            <span style={styles.rightPeopleNum}>/1名</span>
                          </div>
                        ) : (
                          <div>
                            <span style={styles.rightPrice}>{flight.priceDiffPerPerson() >= 0 ? '+' : '-'}</span>
                            <span style={styles.rightPrice}>
                              <span data-wovn-ignore>{digits(Math.abs(flight.priceDiffPerPerson()))}</span>
                              <Yen>円</Yen>
                            </span>
                            <span style={styles.rightPeopleNum}>/1名</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="select-transport-box__right flight disabled">
                    <div style={styles.rightBody}>期限内に発券できません</div>
                  </div>
                )}
                <BoxFooter
                  selected={selected}
                  style={inList && !isDomestic ? styles.flightListInternationalFooter : undefined}
                >
                  {footerContent}
                </BoxFooter>
              </>
            ) : (
              <div style={styles.flightBoxWrap}>
                <div style={styles.outlinePriceRow}>
                  <BoxPriceArea>
                    <BoxPriceWrap>
                      {flightIndex === 0 ? (
                        <BoxPrice>
                          <span data-wovn-ignore>{digits(flight.pricePerPerson())}</span>
                          <Yen>円</Yen>
                        </BoxPrice>
                      ) : (
                        [
                          <BoxPrice key={0}>{flight.priceDiffPerPerson() >= 0 ? '+' : '-'}</BoxPrice>,
                          <BoxPrice key={1}>
                            <span data-wovn-ignore>{digits(Math.abs(flight.priceDiffPerPerson()))}</span>
                            <Yen>円</Yen>
                          </BoxPrice>
                        ]
                      )}
                      <BoxSmall>/1名</BoxSmall>
                    </BoxPriceWrap>
                  </BoxPriceArea>
                  {!isRequestForm && (
                    <BoxButtonArea style={styles.outlineButtonArea}>
                      <BoxButton selected={selected} onClick={handleChange} style={styles.outlineButton}>
                        <BoxButtonText>
                          {selected ? (
                            <ChangeCancelButtonLabel />
                          ) : (
                            <OtherCandidatesButtonLabel candidateCount={candidateCount} />
                          )}
                        </BoxButtonText>
                      </BoxButton>
                    </BoxButtonArea>
                  )}
                </div>
                <BoxFooter selected={selected}>{footerContent}</BoxFooter>
              </div>
            )}
          </div>
          {hasTicketingDeadline && (
            <div className="select-transport-box__right-deadline" style={styles.deadlineBottom}>
              <div
                title="発券期限は航空会社により予告なく変更される場合があります"
                style={{ ...styles.deadlineWrap, marginTop: 0, cursor: 'pointer' }}
              >
                発券期限：
                <span
                  style={{
                    color: theme.tokens.colors.danger.primary,
                    fontWeight: 'bold',
                    fontSize: 12
                  }}
                >
                  {ticketingDeadlineInner}
                </span>
              </div>
            </div>
          )}
        </>
      ) : (
        <Loading />
      )}
    </div>
  );
};

const getStyles = (
  theme: AITTheme,
  isMobile: boolean,
  selected: boolean,
  isDomestic: boolean,
  isSelectSp: boolean
) => ({
  flightBox: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    border: `2px solid ${selected ? theme.linkColor : theme.grayBorderColor}`,
    backgroundColor: 'white',
    padding: 0,
    marginBottom: 10,
    fontSize: 10,
    cursor: 'pointer'
  },
  flightMainColumn: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    width: '100%'
  },
  outlinePriceRow: {
    display: 'flex' as const,
    flexDirection: isSelectSp ? ('column' as const) : ('row' as const),
    alignItems: isSelectSp ? ('stretch' as const) : undefined,
    gap: isSelectSp ? 8 : undefined,
    width: '100%',
    boxSizing: 'border-box' as const,
    minWidth: isMobile ? ('auto' as const) : ('170px' as const),
    marginLeft: isMobile ? 0 : undefined,
    padding: isSelectSp
      ? isMobile && isDomestic
        ? ('16px 0' as const)
        : ('16px 10px' as const)
      : isMobile && !isDomestic
      ? ('10px' as const)
      : isMobile
      ? ('10px 0' as const)
      : ('10px' as const),
    backgroundColor: theme.grayBgColorLight
  },
  outlineButtonArea: isSelectSp
    ? ({
        width: '100%',
        margin: 0
      } as const)
    : undefined,
  outlineButton: isSelectSp
    ? ({
        width: '100%',
        boxSizing: 'border-box' as const,
        justifyContent: 'center' as const
      } as const)
    : undefined,
  deadlineBottom: {
    paddingTop: 8,
    paddingBottom: 8
  },
  flightBoxWrap: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    justifyContent: 'center' as const
  },
  deadlineWrap: {
    display: 'flex' as const,
    alignItems: 'center' as const,
    marginTop: 8,
    fontSize: 12,
    color: theme.grayTextColor
  },
  // 出発・到着・座席の行レイアウト
  flightRow: {
    display: 'flex' as const,
    alignItems: isMobile ? ('flex-start' as const) : ('center' as const),
    height: isMobile ? ('auto' as const) : '2rem',
    minHeight: isMobile ? '2rem' : undefined,
    paddingTop: isMobile ? '2px' : undefined,
    paddingBottom: isMobile ? '2px' : undefined
  },
  // 出発・到着・座席ラベル（国内の boxTitle と統一）
  boxTitle: {
    background: '#aaa',
    color: 'white',
    marginRight: '10px',
    width: '40px',
    height: '20px',
    fontSize: '12px',
    fontWeight: 'bold' as const,
    display: 'flex' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    lineHeight: '1',
    flexShrink: 0
  },
  // 出発・到着の情報テキスト
  flightInfo: {
    fontSize: '14px',
    flex: isMobile ? 1 : undefined,
    minWidth: isMobile ? 0 : undefined,
    wordBreak: isMobile ? ('break-word' as const) : undefined,
    lineHeight: isMobile ? 1.4 : undefined
  },
  // 座席クラス行（display: flex + 縦中央揃え）
  cabinRow: {
    display: 'flex' as const,
    alignItems: 'center' as const,
    marginTop: '3px'
  },
  // 座席クラス名テキスト
  cabinText: {
    marginLeft: '10px',
    fontSize: '14px'
  },
  badgeRow: {
    display: 'flex' as const,
    flexWrap: 'wrap' as const,
    gap: 6,
    padding: '8px 10px 0'
  },
  corporateBadge: {
    display: 'inline-block' as const,
    backgroundColor: '#1b7ab3',
    color: '#fff',
    fontSize: 11,
    fontWeight: 700 as const,
    padding: '2px 8px',
    borderRadius: 2
  },
  publishedBadge: {
    display: 'inline-block' as const,
    backgroundColor: '#6b7280',
    color: '#fff',
    fontSize: 11,
    fontWeight: 700 as const,
    padding: '2px 8px',
    borderRadius: 2
  },
  lowestBadge: {
    display: 'inline-block' as const,
    backgroundColor: '#15803d',
    color: '#fff',
    fontSize: 11,
    fontWeight: 700 as const,
    padding: '2px 8px',
    borderRadius: 2
  },
  rightButton: (selected: boolean, isPackage?: true | null, isDomestic?: boolean) => ({
    display: 'flex' as const,
    alignItems: 'center' as const,
    flexShrink: 0,
    border: `2px solid ${theme.linkColor}`,
    borderRadius: 3,
    padding: 2,
    marginInline: 'auto',
    color: theme.linkColor,
    fontWeight: 'bold' as const,
    cursor: 'pointer' as const,
    height: '3rem',
    width: 'calc(100% - 10px * 2)' as const,
    ...(selected && {
      background: theme.linkColor,
      color: 'white'
    }),
    ...(isPackage && {
      minWidth: 145,
      minHeight: 45,
      textAlign: 'center' as const
    })
  }),
  rightBody: {
    textAlign: 'center' as const,
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  rightPrice: {
    fontSize: isMobile && !isDomestic ? 18 : 16
  },
  rightPeopleNum: {
    fontSize: 11
  },
  flightListInternationalFooter: {
    marginTop: 10,
    paddingTop: 10
  } as const
});

export default FlightBox;
